import styled from "@emotion/styled";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";

import FlowActioner from "services/flow/actioner";
import FlowUtils from "services/flow/utils";
import Abilities from "services/permissions/abilities";

import InOutDistributionLayout from "scenes/Flow/components/InOutDistributionLayout";
import PathDistributionLayout from "scenes/Flow/components/PathDistributionLayout/PathDistributionLayout";

import ChronologyChart from "components/ChronologyChart";
import HourRangeSlider from "components/HourRangeSlider";
import Section from "components/Section";
import StatsChart from "components/StatsChart";
import StatsRow from "components/StatsRow";
import ToggleButtonGroup from "components/ToggleButtonGroup";
import CommonUtils from "services/common/utils";
import ContractUtils from "services/contract/utils";

import ParkkiTheme from "themes/ParkkiTheme";
import { GraphProvider } from "../../../../services/graphs";

const AttendanceFiltersContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
`;

const ChronologyFiltersContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
`;

const CategoryCountFiltersContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
`;

const TrafficChartFiltersContainer = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 10px;
  align-items: center;
  justify-content: center;
`;

const TrafficChartFilterSubContainer = styled.div`
  display: flex;
  flex: 1;
`;

const AttendanceFilterSubContainer = styled.div`
  display: flex;
  flex: 1;
`;

const ChronologyFilterSubContainer = styled.div`
  display: flex;
  flex: 1;
`;

const CategoryCountFilterSubContainer = styled.div`
  display: flex;
  flex: 1;
`;

const InOutDistributionContainer = styled.div`
  margin-bottom: 10px;
`;

function FlowStatsSection({
  contract,
  attendanceAxis,
  travelTimeAxis,
  flow,
  selectedLayer,
  startDate,
  endDate,
  maximumDurationInMinutes,
  attendanceHourRange,
  travelTimeHourRange,
  setAttendanceFilter,
  setTravelTimeFilter,
  inOutDistributionByPercentage,
  setInOutDistributionByPercentage,
  retrieveAttendanceGraph,
  isMultipleDays,
  chronologyAxis,
  setChronologyTimeFilter,
  categoryCountAxis,
  setCategoryCountAxis,
  setTrafficChartAxis,
  trafficChartAxis,
  trafficHourRange,
}) {
  const intl = useIntl();
  const [attendanceViewMode, setAttendanceViewMode] = useState("HOUR");

  const toggleFlowSection = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return Abilities.ability.can("read", "flow.paths.keys");
    }

    return true;
  }, [selectedLayer]);

  const atLeastOneDistributionStatsInPoint = useMemo(() => {
    if (selectedLayer?.type === "area") {
      return true;
    }

    return (
      Abilities.ability.can("read", "flow.distributionPoint.percentageBar") ||
      Abilities.ability.can("read", "flow.distributionPoint.threshold")
    );
  }, [selectedLayer]);

  const togglePathDistributionChart = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return (
        selectedLayer?.apiType === "FLOW" &&
        Abilities.ability.can("read", "flow.paths.distribution")
      );
    }

    return false;
  }, [selectedLayer]);

  const togglePathTravelTimeChart = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return (
        selectedLayer?.apiType === "FLOW" &&
        Abilities.ability.can("read", "flow.paths.graph_travel_time")
      );
    }

    return false;
  }, [selectedLayer]);

  const toggleDistributionLayout = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return false;
    }

    return (
      Abilities.ability.can(
        "read",
        `flow.distribution${selectedLayer?.type === "area" ? "Area" : "Point"}`
      ) && atLeastOneDistributionStatsInPoint
    );
  }, [atLeastOneDistributionStatsInPoint, selectedLayer]);

  const toggleChronologyChart = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return false;
    }

    return Abilities.ability.can(
      "read",
      `flow.chronology${selectedLayer?.type === "area" ? "Area" : "Point"}`
    );
  }, [selectedLayer]);

  const toggleCategoryCountChart = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return false;
    }

    return Abilities.ability.can(
      "read",
      `flow.categoryCount${selectedLayer?.type === "area" ? "Area" : "Point"}`
    );
  }, [selectedLayer]);

  const toggleStatsChart = useMemo(() => {
    if (selectedLayer?.type === "path") {
      return false;
    }

    return Abilities.ability.can(
      "read",
      `flow.numberOfCounts${selectedLayer?.type === "area" ? "Area" : "Point"}`
    );
  }, [selectedLayer]);

  const toggleTrafficStatsChart = useMemo(() => {
    if (
      selectedLayer?.type === "path" &&
      selectedLayer?.apiType === "TRAFFIC"
    ) {
      return true;
    }

    return false;
  }, [selectedLayer]);

  const trafficDetails = useMemo(
    () => flow?.traffic?.[selectedLayer?.point_id],
    [flow?.traffic, selectedLayer?.point_id]
  );

  const generalStatsRow = FlowUtils.getGeneralStatsRow(
    flow?.general,
    trafficDetails
  );
  const attendanceLimitTime = FlowUtils.getAttendanceLimitTime(flow);
  const travelTimeLimit = FlowUtils.getTravelTimeLimit(flow);
  const trafficLimitTime = FlowUtils.getTrafficLimitTime(flow);
  const calendarTypeOptions = useMemo(
    () => [
      { label: intl.messages["calendar.byDay"], value: "DAY" },
      { label: intl.messages["calendar.byHour"], value: "HOUR" },
      { label: intl.messages["calendar.byFiveMin"], value: "5MIN" },
    ],
    [intl]
  );

  const calendarChronologyOptions = useMemo(() => {
    return [
      { label: intl.messages["calendar.over24"], value: "24H" },
      { label: intl.messages["calendar.byHour"], value: "HOUR" },
      { label: intl.messages["calendar.byDay"], value: "DAY" },
    ];
  }, [calendarTypeOptions]);

  const inOutDataTypeOptions = useMemo(
    () => [
      {
        label: intl.messages["flowScene.inOutDistribution.byValue"],
        value: false,
      },
      {
        label: intl.messages["flowScene.inOutDistribution.byPercentage"],
        value: true,
      },
    ],
    [intl]
  );
  const maximumDurationTooltipLabel = useMemo(() => {
    if (!maximumDurationInMinutes || selectedLayer?.type !== "path") {
      return null;
    }

    return intl.formatMessage(
      {
        id: "flowScene.infoTooltipMaximumDuration",
      },
      {
        duration: CommonUtils.humanizeDateFromDuration(
          maximumDurationInMinutes,
          "minutes",
          intl,
          false
        ),
      }
    );
  }, [intl, selectedLayer?.type, maximumDurationInMinutes]);

  function handleAttendanceRangeChange(range) {
    let toHourInSeconds;
    const fromRange =
      range?.[0] !== null && range?.[0] !== undefined ? range?.[0] : 9;
    const toRange = range?.[1] || 18;

    if (toRange === 24) {
      toHourInSeconds = toRange * 3600 - 5 * 60; // 23h55
    }

    setAttendanceFilter("range", {
      fromHour: fromRange * 3600,
      toHour: toRange === 24 ? toHourInSeconds : toRange * 3600,
    });
  }

  const handleTrafficRangeChange = useCallback(
    (range) => {
      let toHourInSeconds;
      const fromRange =
        range?.[0] !== null && range?.[0] !== undefined ? range?.[0] : 9;
      const toRange = range?.[1] || 18;

      if (toRange === 24) {
        toHourInSeconds = toRange * 3600 - 5 * 60; // 23h55
      }

      setTrafficChartAxis("range", {
        fromHour: fromRange * 3600,
        toHour: toRange === 24 ? toHourInSeconds : toRange * 3600,
      });
    },
    [setTrafficChartAxis]
  );

  function handleTravelTimeRangeChange(range) {
    let toHourInSeconds;
    const fromRange =
      range?.[0] !== null && range?.[0] !== undefined ? range?.[0] : 9;
    const toRange = range?.[1] || 18;

    if (toRange === 24) {
      toHourInSeconds = toRange * 3600 - 5 * 60; // 23h55
    }

    setTravelTimeFilter("range", {
      fromHour: fromRange * 3600,
      toHour: toRange === 24 ? toHourInSeconds : toRange * 3600,
    });
  }

  useEffect(() => {
    if (toggleStatsChart && isMultipleDays) {
      setAttendanceViewMode("HOUR");
      setAttendanceFilter("x_axis", "HOUR");
    }
  }, [toggleStatsChart, isMultipleDays, setAttendanceFilter]);

  const handleAttendanceViewModeChange = useCallback(
    (e, value) => {
      if (value === null || value === attendanceViewMode) return;
      setAttendanceViewMode(value);
      if (value === "HOUR" && !isMultipleDays) {
        setAttendanceFilter("group_by_hours", true);
      }
      setAttendanceFilter("x_axis", value);
    },
    [attendanceViewMode, isMultipleDays, setAttendanceFilter]
  );

  const getAvailableViewModes = useCallback(() => {
    if (isMultipleDays) {
      return calendarTypeOptions.filter((option) => option.value !== "5MIN");
    }

    return calendarTypeOptions.filter((option) => option.value !== "DAY");
  }, [isMultipleDays, calendarTypeOptions]);

  return (
    <>
      {toggleFlowSection && (
        <Section
          title={intl.formatMessage(
            {
              id: `flowScene.flowSection.title.${
                selectedLayer?.type === "path"
                  ? selectedLayer?.apiType === "FLOW"
                    ? "path"
                    : "traffic"
                  : "default"
              }`,
            },
            { name: selectedLayer?.long_name }
          )}
          infoTooltip={maximumDurationTooltipLabel}
          subtitle={intl.formatMessage(
            {
              id: "flowScene.flowSection.subtitle",
            },
            {
              from: moment(startDate).format("D MMMM"),
              to: moment(endDate).format("D MMMM YYYY"),
            }
          )}
          style={{
            subtitle: { color: ParkkiTheme.colors.turquoise },
          }}
        >
          <StatsRow
            initialized={!!flow?.general?.initialized}
            stats={generalStatsRow}
            containerStyles={{
              display: "flex",
              flexDirection: "row",
            }}
            numberOfSection={
              generalStatsRow?.filter((row) => row.toggle).length
            }
          />
        </Section>
      )}

      {toggleDistributionLayout && (
        <Section
          title={intl.formatMessage(
            { id: "flowScene.inOutDistribution.title" },
            {
              in:
                trafficDetails?.labels?.in ??
                intl.messages["flowScene.inOutDistribution.in"],
              out:
                trafficDetails?.labels?.out ??
                intl.messages["flowScene.inOutDistribution.out"],
            }
          )}
        >
          <InOutDistributionContainer>
            <ToggleButtonGroup
              exclusive
              loading={!flow.attendance.initialized}
              options={inOutDataTypeOptions}
              value={inOutDistributionByPercentage}
              onChange={(e, value) => {
                if (value === null) return;
                return setInOutDistributionByPercentage(
                  "inOutDistributionByPercentage",
                  value
                );
              }}
            />
          </InOutDistributionContainer>
          <InOutDistributionLayout
            disabled={
              Number.isNaN(flow?.details?.percentageIn) ||
              Number.isNaN(flow?.details?.percentageOut)
            }
            initialized={!!flow?.details?.initialized}
            stats={flow?.details}
            trafficDetails={trafficDetails}
          />
        </Section>
      )}

      {togglePathDistributionChart && (
        <Section
          title={intl.messages["flowScene.pathDistribution.title"]}
          infoTooltip={maximumDurationTooltipLabel}
        >
          <InOutDistributionContainer>
            <ToggleButtonGroup
              exclusive
              loading={!flow.attendance.initialized}
              options={inOutDataTypeOptions}
              value={inOutDistributionByPercentage}
              onChange={(e, value) => {
                if (value === null) return;
                return setInOutDistributionByPercentage(
                  "inOutDistributionByPercentage",
                  value
                );
              }}
            />
          </InOutDistributionContainer>
          <PathDistributionLayout
            disabled={
              Number.isNaN(flow?.details?.percentageIn) ||
              Number.isNaN(flow?.details?.percentageOut)
            }
            initialized={!!flow?.distribution?.initialized}
            stats={flow?.distribution}
          />
        </Section>
      )}

      {toggleChronologyChart && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedLayer?.long_name}
        >
          <Section title={intl.messages["flowScene.charts.chronology.title"]}>
            {isMultipleDays && (
              <ChronologyFiltersContainer>
                <ChronologyFilterSubContainer>
                  <ToggleButtonGroup
                    exclusive
                    loading={!flow.travelTime.initialized}
                    options={calendarChronologyOptions}
                    value={chronologyAxis}
                    onChange={(e, value) => {
                      if (value === null) return;
                      return setChronologyTimeFilter("x_axis", value);
                    }}
                  />
                </ChronologyFilterSubContainer>
              </ChronologyFiltersContainer>
            )}
            <ChronologyChart
              initialized={flow?.chronology.initialized}
              stats={FlowUtils.getChronologyChart(flow, intl, chronologyAxis)}
              graphOptions={FlowUtils.getChronologyChartOptions(flow, intl)}
            />
          </Section>
        </GraphProvider>
      )}

      {togglePathTravelTimeChart && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedLayer?.long_name}
        >
          <Section
            style={{ marginTop: "16px" }}
            title={
              intl.messages[
                `flowScene.travelTime.title.${FlowUtils.getTravelTimeChartAxis(
                  flow?.filters
                )?.toLowerCase()}`
              ]
            }
            infoTooltip={maximumDurationTooltipLabel}
          >
            <AttendanceFiltersContainer>
              <AttendanceFilterSubContainer>
                <ToggleButtonGroup
                  exclusive
                  loading={!flow.travelTime.initialized}
                  options={calendarTypeOptions.filter((o) => o.value !== "DAY")}
                  value={travelTimeAxis}
                  onChange={(e, value) => {
                    if (value === null) return;
                    return setTravelTimeFilter("x_axis", value);
                  }}
                />
              </AttendanceFilterSubContainer>

              {FlowUtils.getTravelTimeChartAxis(flow?.filters) === "5MIN" && (
                <AttendanceFilterSubContainer>
                  <HourRangeSlider
                    value={travelTimeHourRange}
                    onChange={handleTravelTimeRangeChange}
                    min={travelTimeLimit.left_time_limit}
                    max={travelTimeLimit.right_time_limit}
                  />
                </AttendanceFilterSubContainer>
              )}
            </AttendanceFiltersContainer>

            <StatsChart
              initialized={flow?.travelTime?.initialized}
              stats={FlowUtils.getTravelTimeChart(flow, intl)}
              graphOptions={FlowUtils.getTravelTimeChartOptions(flow, intl)}
            />
          </Section>
        </GraphProvider>
      )}

      {toggleCategoryCountChart && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedLayer?.long_name}
        >
          <Section
            title={intl.messages["flowScene.charts.categoryCount.title"]}
          >
            <CategoryCountFiltersContainer>
              <CategoryCountFilterSubContainer>
                <ToggleButtonGroup
                  exclusive
                  loading={!flow.categoryCount.initialized}
                  options={calendarTypeOptions.filter(
                    (o) => o.value !== "5MIN"
                  )}
                  value={categoryCountAxis}
                  onChange={(e, value) => {
                    if (value === null) return;
                    return setCategoryCountAxis("x_axis", value);
                  }}
                />
              </CategoryCountFilterSubContainer>
            </CategoryCountFiltersContainer>
            <StatsChart
              initialized={flow?.categoryCount?.initialized}
              stats={FlowUtils.getCategoryCountChart(flow, intl)}
              graphOptions={FlowUtils.getCategoryCountChartOptions(flow, intl)}
            />
          </Section>
        </GraphProvider>
      )}

      {toggleTrafficStatsChart && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedLayer?.long_name}
        >
          <Section title={intl.messages["flowScene.charts.traffic.title"]}>
            <TrafficChartFiltersContainer>
              <TrafficChartFilterSubContainer>
                <ToggleButtonGroup
                  exclusive
                  loading={!flow.trafficChart.initialized}
                  options={calendarTypeOptions}
                  value={trafficChartAxis}
                  onChange={(e, value) => {
                    if (value === null) return;
                    return setTrafficChartAxis("x_axis", value);
                  }}
                />
              </TrafficChartFilterSubContainer>
              {trafficChartAxis === "5MIN" && (
                <TrafficChartFilterSubContainer>
                  <HourRangeSlider
                    value={trafficHourRange}
                    min={trafficLimitTime?.left_time_limit}
                    max={trafficLimitTime?.right_time_limit}
                    onChange={handleTrafficRangeChange}
                  />
                </TrafficChartFilterSubContainer>
              )}
            </TrafficChartFiltersContainer>
            <StatsChart
              initialized={flow?.trafficChart?.initialized}
              stats={FlowUtils.getTrafficChart(flow, intl)}
              graphOptions={FlowUtils.getTrafficChartOptions(flow, intl)}
            />
          </Section>
        </GraphProvider>
      )}

      {toggleStatsChart && (
        <GraphProvider
          timeTitle={`[${ContractUtils.formatTimestamp(
            contract,
            startDate / 1000
          )}]-[${ContractUtils.formatTimestamp(contract, endDate / 1000)}]`}
          label={selectedLayer?.long_name}
        >
          <Section
            style={{ marginTop: "16px" }}
            title={intl.messages["flowScene.charts.attendance.title"]}
          >
            <AttendanceFiltersContainer>
              <AttendanceFilterSubContainer>
                <ToggleButtonGroup
                  exclusive
                  loading={!flow.attendance.initialized}
                  options={getAvailableViewModes()}
                  value={attendanceViewMode}
                  onChange={handleAttendanceViewModeChange}
                />
              </AttendanceFilterSubContainer>

              {attendanceViewMode === "5MIN" && !isMultipleDays && (
                <AttendanceFilterSubContainer>
                  <HourRangeSlider
                    value={attendanceHourRange}
                    min={attendanceLimitTime?.left_time_limit}
                    max={attendanceLimitTime?.right_time_limit}
                    onChange={handleAttendanceRangeChange}
                  />
                </AttendanceFilterSubContainer>
              )}
            </AttendanceFiltersContainer>
            <StatsChart
              initialized={flow?.attendance?.initialized}
              stats={FlowUtils.getAttendanceChart(flow, intl)}
              graphOptions={FlowUtils.getAttendanceChartOptions(flow, intl)}
            />
          </Section>
        </GraphProvider>
      )}
    </>
  );
}

const mapStateToProps = (state) => ({
  contract: state.Contract.contract,
  flow: state.Flow,
  selectedLayer: FlowUtils.getSelectedLayer(state.Flow),
  startDate: state.Flow?.filters?.from * 1000,
  endDate: state.Flow?.filters?.to * 1000,
  maximumDurationInMinutes: state.Flow?.filters?.maximumDurationInMinutes,
  attendanceAxis: state?.Flow?.filters?.attendance?.x_axis,
  chronologyAxis: state?.Flow?.filters?.chronology?.x_axis,
  travelTimeAxis: state?.Flow?.filters?.travelTime?.x_axis,
  categoryCountAxis: state?.Flow?.filters?.categoryCount?.x_axis,
  trafficChartAxis: state?.Flow?.filters?.trafficChart?.x_axis,
  trafficHourRange: FlowUtils.getTrafficHourRange(state.Flow),
  attendanceHourRange: FlowUtils.getAttendanceHourRange(state.Flow),
  travelTimeHourRange: FlowUtils.getTravelTimeHourRange(state.Flow),
  inOutDistributionByPercentage:
    state?.Flow.filters.inOutDistributionByPercentage,
});

const mapDispatchToProps = (dispatch) => ({
  setAttendanceFilter: (key, value) =>
    dispatch(FlowActioner.setAttendanceFilter(key, value)),
  setTravelTimeFilter: (key, value) =>
    dispatch(FlowActioner.setTravelTimeFilter(key, value)),
  setChronologyTimeFilter: (key, value) =>
    dispatch(FlowActioner.setChronologyTimeFilter(key, value)),
  setInOutDistributionByPercentage: (key, value) =>
    dispatch(FlowActioner.setFilter(key, value)),
  setCategoryCountAxis: (key, value) =>
    dispatch(FlowActioner.setCategoryCountTimeFilter(key, value)),
  setTrafficChartAxis: (key, value) =>
    dispatch(FlowActioner.setTrafficChartTimeFilter(key, value)),
  retrieveAttendanceGraph: (layer, options) =>
    dispatch(FlowActioner.retrieveAttendanceGraph(layer, options)),
});

export default connect(mapStateToProps, mapDispatchToProps)(FlowStatsSection);
